import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BookingRequestDto, BookingRequestStatus } from '@motoschool/shared';
import { BOOKING_REQUEST_STATUS_LABELS } from '@motoschool/shared';
import { api, ApiRequestError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  PageLoader,
  Sheet,
  SuccessNotice,
  Textarea,
  cn,
} from '../components/ui';
import { formatDateTime } from '../lib/format';

const STATUS_TONE: Record<BookingRequestStatus, 'warning' | 'success' | 'danger' | 'neutral'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
  EXPIRED: 'neutral',
};

const FILTERS: Array<{ value: BookingRequestStatus | 'ALL'; label: string }> = [
  { value: 'PENDING', label: 'Ожидают' },
  { value: 'APPROVED', label: 'Подтверждены' },
  { value: 'REJECTED', label: 'Отклонены' },
  { value: 'ALL', label: 'Все' },
];

interface RequestsResponse {
  items: BookingRequestDto[];
  total: number;
}

interface CreatedStudent {
  name: string;
  phone: string | null;
  password: string;
}

export function RequestsPage(): React.JSX.Element {
  const { hasRole, timeZone } = useAuth();
  const isStaff = hasRole('TEACHER', 'ADMIN');
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<BookingRequestStatus | 'ALL'>('PENDING');
  const [decision, setDecision] = useState<{ request: BookingRequestDto; approve: boolean } | null>(
    null,
  );
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createdStudent, setCreatedStudent] = useState<CreatedStudent | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['requests', status],
    queryFn: () =>
      api.get<RequestsResponse>('/requests', {
        status: status === 'ALL' ? undefined : status,
        pageSize: 50,
      }),
  });

  const decide = useMutation({
    mutationFn: async (input: { id: string; approve: boolean }) =>
      api.post<{ createdStudent: CreatedStudent | null }>(`/requests/${input.id}/decide`, {
        decision: input.approve ? 'APPROVED' : 'REJECTED',
        comment: comment || null,
        createStudentAccount: true,
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
      await queryClient.invalidateQueries({ queryKey: ['slots'] });
      setDecision(null);
      setComment('');
      // Если для гостя завели аккаунт, пароль показываем один раз.
      if (result.createdStudent) setCreatedStudent(result.createdStudent);
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiRequestError ? caught.message : 'Не удалось обработать заявку');
    },
  });

  const cancelOwn = useMutation({
    mutationFn: (id: string) => api.post(`/requests/${id}/cancel`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['requests'] }),
  });

  const requests = data?.items ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">{isStaff ? 'Заявки на занятия' : 'Мои заявки'}</h1>
        <p className="text-sm text-muted">
          {isStaff
            ? 'Подтвердите запись — время закрепится за учеником'
            : 'Здесь видно, что ответил инструктор'}
        </p>
      </div>

      <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
        {FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setStatus(filter.value)}
            className={cn(
              'min-h-9 shrink-0 rounded-full border px-3 text-sm font-medium transition-colors',
              status === filter.value
                ? 'border-brand-600 bg-brand-600 text-white'
                : 'border-line bg-surface text-muted',
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {createdStudent && (
        <SuccessNotice>
          <p className="font-medium">Создан аккаунт для ученика {createdStudent.name}</p>
          <p className="mt-1">
            Логин: {createdStudent.phone ?? '—'} · Пароль: <b>{createdStudent.password}</b>
          </p>
          <p className="mt-1 text-xs">
            Пароль показывается один раз — передайте его ученику.{' '}
            <button type="button" className="underline" onClick={() => setCreatedStudent(null)}>
              Скрыть
            </button>
          </p>
        </SuccessNotice>
      )}

      {isLoading ? (
        <PageLoader />
      ) : requests.length === 0 ? (
        <EmptyState
          title="Заявок нет"
          description={
            isStaff
              ? 'Поделитесь ссылкой на расписание — заявки появятся здесь.'
              : 'Выберите свободное время в расписании и отправьте заявку.'
          }
        />
      ) : (
        <div className="space-y-3">
          {requests.map((request) => {
            const applicant = request.student?.name ?? request.guestName ?? 'Гость';
            const contact = request.student?.phone ?? request.guestPhone ?? request.guestTelegram;

            return (
              <Card key={request.id}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <Avatar name={applicant} size="sm" />
                    <div className="min-w-0">
                      <p className="truncate font-medium">{applicant}</p>
                      {contact && <p className="truncate text-xs text-muted">{contact}</p>}
                    </div>
                  </div>
                  <Badge tone={STATUS_TONE[request.status]}>
                    {BOOKING_REQUEST_STATUS_LABELS[request.status]}
                  </Badge>
                </div>

                <p className="mt-3 text-sm">
                  <span className="text-muted">Занятие: </span>
                  {formatDateTime(request.slot.startsAt, timeZone)}
                  {request.slot.location ? ` · ${request.slot.location}` : ''}
                </p>

                {request.comment && (
                  <p className="mt-2 rounded-xl bg-canvas px-3 py-2 text-sm">{request.comment}</p>
                )}

                {request.decisionComment && (
                  <p className="mt-2 text-sm text-muted">Ответ: {request.decisionComment}</p>
                )}

                {isStaff && request.status === 'PENDING' && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => {
                        setError(null);
                        setDecision({ request, approve: true });
                      }}
                    >
                      Подтвердить
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setError(null);
                        setDecision({ request, approve: false });
                      }}
                    >
                      Отклонить
                    </Button>
                  </div>
                )}

                {!isStaff && request.status === 'PENDING' && (
                  <Button
                    variant="secondary"
                    fullWidth
                    className="mt-3"
                    isLoading={cancelOwn.isPending}
                    onClick={() => cancelOwn.mutate(request.id)}
                  >
                    Отозвать заявку
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Sheet
        isOpen={decision !== null}
        title={decision?.approve ? 'Подтвердить запись' : 'Отклонить заявку'}
        onClose={() => setDecision(null)}
        footer={
          <div className="flex gap-2 pb-1">
            <Button variant="secondary" fullWidth onClick={() => setDecision(null)}>
              Отмена
            </Button>
            <Button
              fullWidth
              isLoading={decide.isPending}
              onClick={() =>
                decision && decide.mutate({ id: decision.request.id, approve: decision.approve })
              }
            >
              {decision?.approve ? 'Подтвердить' : 'Отклонить'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {error && <ErrorNotice message={error} />}

          {decision && (
            <p className="text-sm text-muted">
              {decision.request.student?.name ?? decision.request.guestName} ·{' '}
              {formatDateTime(decision.request.slot.startsAt, timeZone)}
            </p>
          )}

          <Field
            label="Комментарий"
            hint={
              decision?.approve
                ? 'Ученик увидит его в уведомлении'
                : 'Объясните причину — так понятнее, что делать дальше'
            }
          >
            <Textarea value={comment} onChange={(event) => setComment(event.target.value)} />
          </Field>

          {decision?.approve && decision.request.student === null && (
            <p className="rounded-xl bg-brand-50 px-3 py-2 text-sm text-brand-700">
              Заявка пришла по ссылке. При подтверждении ученику будет создан аккаунт,
              пароль покажется на экране один раз.
            </p>
          )}
        </div>
      </Sheet>
    </div>
  );
}
